import request from '@/utils/request'

/**
 * 桥梁通航记录查询
 * @param {Object} params - 查询条件
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} [params.startTime] - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.endTime] - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.eventType] - 事件类型
 * @param {string} [params.vesselType] - 船舶类型
 * @param {number} [params.status] - 处理状态（0-正常，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度
 * @param {number} params.pageNum - 页码
 * @param {number} params.pageSize - 页大小
 * @returns {Promise<{code: number, message: string, data: {list: Array<{
 *   id: number,
 *   bridgeId: number,
 *   bridgeName: string,
 *   areaId: number,
 *   areaName: string,
 *   vesselMmsi: string,
 *   vesselName: string,
 *   vesselType: number,
 *   vesselTypeName: string,
 *   eventTime: string,
 *   eventType: string,
 *   eventTypeDescription: string,
 *   eventSubtype: string,
 *   longitude: number,
 *   latitude: number,
 *   speed: number,
 *   heading: number,
 *   approachDirection: number,
 *   approachDirectionName: string,
 *   status: number,
 *   statusName: string,
 *   severityLevel: number,
 *   severityLevelName: string,
 *   eventValue: number,
 *   eventThreshold: number,
 *   handleTime: string,
 *   handleUser: number,
 *   handleUserName: string,
 *   handleResult: string,
 *   sourceType: string,
 *   sourceId: string,
 *   sourceName: string,
 *   alertId: number,
 *   createTime: string,
 *   updateTime: string,
 *   extraData: object
 * }>, pageNum: number, pageSize: number, pages: number, total: number}}>} 桥梁通航记录分页数据
 */
export function getBridgeCrossingRecords(params = {}) {
  return request({
    url: '/comprehensive-query/records/bridge-crossing',
    method: 'post',
    data: params
  })
}

/**
 * 航道进出记录查询
 * @param {Object} params - 查询条件，同getBridgeCrossingRecords
 * @returns {Promise<{code: number, message: string, data: Object}>} 航道进出记录分页数据
 */
export function getChannelEntryExitRecords(params = {}) {
  return request({
    url: '/comprehensive-query/records/channel-entry-exit',
    method: 'post',
    data: params
  })
}

/**
 * 通航区进出记录查询
 * @param {Object} params - 查询条件，同getBridgeCrossingRecords
 * @returns {Promise<{code: number, message: string, data: Object}>} 通航区进出记录分页数据
 */
export function getNavigationAreaRecords(params = {}) {
  return request({
    url: '/comprehensive-query/records/navigation-area',
    method: 'post',
    data: params
  })
}

/**
 * 报告线记录查询
 * @param {Object} params - 查询条件，同getBridgeCrossingRecords
 * @returns {Promise<{code: number, message: string, data: Object}>} 报告线记录分页数据
 */
export function getReportLineRecords(params = {}) {
  return request({
    url: '/comprehensive-query/records/report-line',
    method: 'post',
    data: params
  })
}

/**
 * 禁航区进出记录查询
 * @param {Object} params - 查询条件，同getBridgeCrossingRecords
 * @returns {Promise<{code: number, message: string, data: Object}>} 禁航区进出记录分页数据
 */
export function getRestrictedAreaRecords(params = {}) {
  return request({
    url: '/comprehensive-query/records/restricted-area',
    method: 'post',
    data: params
  })
}

/**
 * 警戒区进出记录查询
 * @param {Object} params - 查询条件，同getBridgeCrossingRecords
 * @returns {Promise<{code: number, message: string, data: Object}>} 警戒区进出记录分页数据
 */
export function getWarningAreaRecords(params = {}) {
  return request({
    url: '/comprehensive-query/records/warning-area',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁流量统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: object
 *   }>,
 *   vesselTypeData: Array<{
 *     vesselTypeCode: string,
 *     vesselTypeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁流量统计结果
 */
export function getTrafficFlowStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/traffic-flow',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁预警统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: {
 *       alertLevels: Array<{level: string, count: number}> | null,
 *       eventTypeCounts: {[key: string]: number} | null,
 *       avgSeverity: number,
 *       maxSeverity: number
 *     }
 *   }>,
 *   levelData: Array<{
 *     levelCode: string,
 *     levelName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   typeData: Array<{
 *     typeCode: string,
 *     typeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   statusData: Array<{
 *     statusCode: string,
 *     statusName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁预警统计结果
 * 
 * extraData.eventTypeCounts 说明：
 * - ENTRY: 进入事件数量
 * - EXIT: 离开事件数量
 * - CROSSING: 穿越事件数量
 * - YAW: 偏航事件数量
 * - SPEED: 超速事件数量
 * - HIGH: 超高事件数量
 * - STAY: 滞留事件数量
 * - BLACKLIST: 黑名单事件数量
 */
export function getComprehensiveAlertStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/comprehensive-alert',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁偏航统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: object
 *   }>,
 *   vesselTypeData: Array<{
 *     vesselTypeCode: string,
 *     vesselTypeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁偏航统计结果
 */
export function getYawDeviationStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/yaw-deviation',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁超高统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: object
 *   }>,
 *   vesselTypeData: Array<{
 *     vesselTypeCode: string,
 *     vesselTypeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁超高统计结果
 */
export function getHeightViolationStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/height-violation',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁超速统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: object
 *   }>,
 *   vesselTypeData: Array<{
 *     vesselTypeCode: string,
 *     vesselTypeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁超速统计结果
 */
export function getSpeedViolationStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/speed-violation',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁禁航统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: object
 *   }>,
 *   vesselTypeData: Array<{
 *     vesselTypeCode: string,
 *     vesselTypeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁禁航统计结果
 */
export function getRestrictedAreaStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/restricted-area',
    method: 'post',
    data: params
  })
}

/**
 * 桥梁VHF统计
 * @param {Object} params - 查询条件
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.vesselMmsi] - 船舶MMSI
 * @param {string} [params.vesselType] - 船舶类型
 * @param {string} [params.eventType] - 事件类型
 * @param {number} [params.status] - 处理状态（0-未处理，1-已处理，2-已忽略）
 * @param {number} [params.severityLevel] - 严重程度（1-5级）
 * @param {string} [params.areaType] - 区域类型
 * @param {number} [params.deduplicationMinutes] - 去重时间间隔（分钟）
 * @param {string} [params.groupBy] - 分组方式
 * @param {string} [params.timeGranularity] - 统计时间粒度（HOUR, DAY, MONTH等）
 * @returns {Promise<{code: number, message: string, data: {
 *   startTime: string,
 *   endTime: string,
 *   totalCount: number,
 *   timeSeriesData: Array<{
 *     time: string,
 *     timeLabel: string,
 *     value: number,
 *     extraData: object
 *   }>,
 *   vesselTypeData: Array<{
 *     vesselTypeCode: string,
 *     vesselTypeName: string,
 *     count: number,
 *     percentage: number,
 *     extraData: object
 *   }>,
 *   detailData: object
 * }}>} 桥梁VHF统计结果
 */
export function getVhfStatistics(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/vhf',
    method: 'post',
    data: params
  })
}

/**
 * 统计详情分页查询
 * @param {Object} params - 查询条件
 * @param {string} params.statisticsType - 统计类型：YAW_DEVIATION、HEIGHT_VIOLATION、SPEED_VIOLATION、TRAFFIC_FLOW、VHF、COMPREHENSIVE_ALERT
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 查询开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 查询结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.mmsiOrName] - 船舶MMSI或船名，用于船舶筛选
 * @param {string} [params.vesselType] - 船舶类型筛选
 * @param {string} [params.alertLevel] - 告警级别筛选
 * @param {number} [params.severityLevel] - 严重级别筛选（1-5）
 * @param {string} [params.communicationType] - 通信类型筛选（仅VHF统计使用）
 * @param {number} [params.pageNum=1] - 页码，默认1
 * @param {number} [params.pageSize=10] - 每页大小，默认10
 * @param {boolean} [params.isExport=false] - 是否为导出请求，true时返回全部数据（最多1000条）
 * @returns {Promise<{code: number, message: string, data: {
 *   list: Array<{
 *     id: number,
 *     bridgeId: number,
 *     bridgeName: string,
 *     areaId: number,
 *     areaName: string,
 *     vesselMmsi: string,
 *     vesselName: string,
 *     vesselType: number,
 *     vesselTypeName: string,
 *     eventTime: string,
 *     eventType: string,
 *     eventSubtype: string,
 *     longitude: number,
 *     latitude: number,
 *     speed: number,
 *     heading: number,
 *     approachDirection: number,
 *     approachDirectionName: string,
 *     status: number,
 *     statusName: string,
 *     severityLevel: number,
 *     severityLevelName: string,
 *     eventValue: number,
 *     eventThreshold: number,
 *     handleTime: string,
 *     handleUser: number,
 *     handleResult: string,
 *     sourceType: string,
 *     sourceId: string,
 *     sourceName: string,
 *     alertId: number,
 *     alertLevel: string,
 *     alertMessage: string,
 *     ruleId: number,
 *     ruleName: string,
 *     ruleType: string,
 *     noticeType: string,
 *     communicationType: string,
 *     communicationTypeName: string,
 *     createTime: string,
 *     extraData: string
 *   }>,
 *   pageNum: number,
 *   pageSize: number,
 *   pages: number,
 *   total: number
 * }}>} 统计详情分页数据
 */
export function getStatisticsDetails(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/details',
    method: 'post',
    data: params
  })
}

/**
 * 统计详情CSV导出
 * @param {Object} params - 导出条件，参数同getStatisticsDetails
 * @param {string} params.statisticsType - 统计类型：YAW_DEVIATION、HEIGHT_VIOLATION、SPEED_VIOLATION、TRAFFIC_FLOW、VHF、COMPREHENSIVE_ALERT
 * @param {number} [params.bridgeId] - 桥梁ID
 * @param {string} params.startTime - 查询开始时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 查询结束时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {string} [params.mmsiOrName] - 船舶MMSI或船名，用于船舶筛选
 * @param {string} [params.vesselType] - 船舶类型筛选
 * @param {string} [params.alertLevel] - 告警级别筛选
 * @param {number} [params.severityLevel] - 严重级别筛选（1-5）
 * @param {string} [params.communicationType] - 通信类型筛选（仅VHF统计使用）
 * @returns {Promise<Blob>} CSV文件的二进制数据
 */
export function exportStatisticsDetailsToCSV(params = {}) {
  return request({
    url: '/comprehensive-query/statistics/details/export',
    method: 'post',
    data: { ...params, isExport: true },
    responseType: 'blob'
  })
}